/**
 * Shared helpers for attendance desk requests (Change Shift, Regularise, Short Leave, On Duty).
 * Pending rows persist to attendance_quick_requests so Admin / RM retrieve them cross-browser.
 */

import {
  activityKeyFromActionLabel,
  isActivityAutoApproved,
  resolveActivityPendingWith,
  resolveActivityWorkflow,
} from "./attendanceActivityWorkflows";
import {
  loadAttendancePolicies,
  resolvePolicyForEmployee,
} from "./attendancePolicyEngine";
import { getLocalDayKey } from "./attendancePunchStorage";
import { resolveChangeShiftPendingWith } from "./attendanceShiftOverride";
import {
  CHANGE_WO_ACTION_LABEL,
  isChangeWoAutoApproved,
  resolveChangeWoPendingWith,
  resolveEmployeeWeeklyOffPolicy,
  validateChangeWoRequest,
} from "./weeklyOffChange";
import { extractReportingManagerEmployeeId } from "./deskApprovalRouting";
import { employeeService } from "../services/api";
import { getAuthItem, getAuthJson } from "../utils/authStorage";
import { isManagerRole, roleDisplayName } from "../utils/workflowStepsBadge";

const STORAGE_KEY = "adminQuickActionRequests";

export function loggedInCompanyId() {
  const fromItem = Number(getAuthItem("companyId"));
  if (Number.isFinite(fromItem) && fromItem > 0) return fromItem;
  const user = getAuthJson("currentUser", null);
  const n = Number(user?.companyId ?? user?.company_id);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Desk rows with no company, or another company, must not render. */
export function isCurrentCompanyRow(row) {
  const companyId = loggedInCompanyId();
  if (!companyId) return false;
  const rowCompany = Number(row?.companyId ?? row?.company_id);
  return rowCompany === companyId;
}

/** Empty draft shared by ESS / My Team / Admin QuickRequestFormModal. */
export function emptyAttendanceDeskDraft() {
  return {
    date: getLocalDayKey(),
    proposedDate: "",
    fromDate: "",
    toDate: "",
    shift: "",
    currentShift: "",
    leaveType: "",
    leaveDuration: "full_day",
    inTime: "",
    outTime: "",
    startTime: "",
    endTime: "",
    resignationReason: "",
    resignationDate: "",
    lastWorkingDate: "",
    reason: "",
    notes: "",
    ticketType: "",
    ticketCategory: "",
    ticketSubcategory: "",
    ticketPriority: "medium",
    ticketSubject: "",
    amount: "",
    investmentSection: "",
    financialYear: "",
    taxRegime: "new",
    positionTitle: "",
    positionsCount: "1",
    requiredByDate: "",
    requestDepartment: "",
    requestDesignation: "",
  };
}

export function loadQuickActionRequests() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const list = Array.isArray(parsed) ? parsed : [];
    return list.filter(isCurrentCompanyRow);
  } catch {
    return [];
  }
}

export function saveQuickActionRequests(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}

export function prependQuickActionRequest(entry) {
  const companyId = loggedInCompanyId();
  const stamped = {
    ...entry,
    companyId: Number(entry?.companyId ?? entry?.company_id) || companyId || null,
  };
  const next = [stamped, ...loadQuickActionRequests()];
  saveQuickActionRequests(next);
  try {
    window.dispatchEvent(
      new CustomEvent("admin:quick-action-submitted", {
        detail: {
          requestId: entry.requestId,
          actionLabel: entry.actionLabel,
          employeeId: entry.employeeId,
          status: entry.status,
          source: entry.source,
          formData: entry.formData,
        },
      })
    );
  } catch {
    /* ignore */
  }
  return stamped;
}

/**
 * Persist desk row to attendance_quick_requests so Admin / RM retrieve it
 * outside the submitter's localStorage (same path as Regularise / Change WO).
 */
export async function persistAttendanceDeskToServer(entry, { raisedByName = null, throwOnError = false } = {}) {
  const isFinalization =
    entry?.actionLabel === "Attendance Lock" ||
    entry?.actionLabel === "Attendance Unlock" ||
    entry?.actionLabel === "Attendance Finalization";
  if (!entry?.requestId || !entry?.actionLabel) return null;
  if (!entry?.employeeId && !isFinalization) return null;
  try {
    const { attendanceService } = await import("../services/api");
    const { getAuthJson, getAuthItem } = await import("../utils/authStorage");
    const currentUser = getAuthJson("currentUser", null);
    const resolvedEmployeeId =
      entry.employeeId ||
      currentUser?.employeeId ||
      currentUser?.employee_id ||
      Number(getAuthItem("employeeId")) ||
      null;
    const res = await attendanceService.createDeskQuickRequest({
      employeeId: resolvedEmployeeId,
      actionLabel: entry.actionLabel,
      requestId: entry.requestId,
      status: entry.status || "Pending",
      formData: entry.formData || {},
      employeeCode: entry.employeeCode || currentUser?.empCode || null,
      employeeName: entry.userName || currentUser?.name || null,
      reportingManagerEmployeeId: entry.reportingManagerEmployeeId || null,
      reportingManagerName: entry.reportingManagerName || null,
    });
    if (entry.status === "Approved") {
      const firstName = currentUser?.name || "";
      const lastName = currentUser?.lastName || "";
      const actorName = [firstName, lastName].filter(Boolean).join(" ") || raisedByName || entry.raisedByName || null;
      await attendanceService.updateDeskQuickRequestStatus(entry.requestId, "Approved", {
        reason: entry.formData?.reason || entry.formData?.notes || null,
        actorName,
      });
    }
    const serverId = res?.data?.id || res?.data?.serverDeskId || null;
    if (serverId) {
      entry.serverDeskId = serverId;
      if (!entry.employeeId && res?.data?.employeeId) {
        entry.employeeId = String(res.data.employeeId);
      }
      entry.source = entry.source || "api";
      try {
        const list = loadQuickActionRequests();
        const idx = list.findIndex((r) => String(r.requestId) === String(entry.requestId));
        if (idx >= 0) {
          list[idx] = {
            ...list[idx],
            serverDeskId: serverId,
            employeeId: entry.employeeId,
            source: list[idx].source === "api" ? "api" : list[idx].source || "api",
          };
          saveQuickActionRequests(list);
        }
      } catch {
        /* ignore */
      }
    }
    return res?.data || null;
  } catch (err) {
    console.warn(
      `${entry.actionLabel} desk persist skipped:`,
      err?.message || err
    );
    if (throwOnError) throw err;
    return null;
  }
}

function newRequestId() {
  return `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Resolve RM for an employee (API).
 * @returns {{ reportingManagerEmployeeId: number|null, reportingManagerName: string|null, noReportingManager: boolean, employee: object|null }}
 */
export async function resolveReportingManagerForEmployee(employeeId) {
  try {
    const empRes = await employeeService.getEmployee(employeeId);
    const employee = empRes?.data || null;
    const reportingManagerEmployeeId = extractReportingManagerEmployeeId(employee);
    let reportingManagerName = extractReportingManagerDisplayName(employee);
    if (reportingManagerEmployeeId && !reportingManagerName) {
      try {
        const rmRes = await employeeService.getEmployee(reportingManagerEmployeeId);
        reportingManagerName = employeeDisplayName(rmRes?.data);
      } catch {
        /* ignore */
      }
    }
    return {
      reportingManagerEmployeeId,
      reportingManagerName: reportingManagerName || null,
      noReportingManager: !reportingManagerEmployeeId,
      employee,
    };
  } catch {
    return {
      reportingManagerEmployeeId: null,
      reportingManagerName: null,
      noReportingManager: true,
      employee: null,
    };
  }
}

function employeeDisplayName(emp) {
  if (!emp || typeof emp !== "object") return null;
  const n =
    emp.user?.name ||
    emp.name ||
    [emp.firstName, emp.lastName].filter(Boolean).join(" ") ||
    emp.fullName ||
    null;
  const text = n != null ? String(n).trim() : "";
  return text || null;
}

function extractReportingManagerDisplayName(employee) {
  if (!employee || typeof employee !== "object") return null;
  const nested = employee.reportingManager;
  if (nested && typeof nested === "object") {
    const fromNested = employeeDisplayName(nested);
    if (fromNested) return fromNested;
  }
  const direct =
    employee.reportingManagerName ||
    employee.reporting_manager_name ||
    employee.managerName ||
    null;
  const text = direct != null ? String(direct).trim() : "";
  return text || null;
}

function resolveEmployeeCode(value) {
  if (!value || typeof value !== "object") return null;
  const code =
    value.empCode ||
    value.employeeCode ||
    value.emp_code ||
    value.employee_code ||
    value.code ||
    null;
  const text = code != null ? String(code).trim() : "";
  return text || null;
}

/**
 * @param {"ess"|"rm_direct"|"admin_direct"} mode
 */
export function buildAttendanceDeskRequest({
  actionLabel,
  employeeId,
  userName,
  userId = null,
  userEmail = "",
  formData = {},
  mode = "ess",
  employeeCode = null,
  raisedByName = null,
  raisedByEmployeeId = null,
  reportingManagerEmployeeId = null,
  reportingManagerName = null,
  noReportingManager = false,
  activityWorkflow = null,
}) {
  const now = Date.now();
  const isDirect = mode === "rm_direct" || mode === "admin_direct";
  const submitter =
    raisedByName ||
    (mode === "admin_direct" ? "Admin" : mode === "rm_direct" ? "Reporting Manager" : userName) ||
    "User";

  const activityKey = activityKeyFromActionLabel(actionLabel);
  const wf =
    activityWorkflow ||
    (activityKey
      ? resolveActivityWorkflow(
          resolvePolicyForEmployee(
            { id: employeeId },
            loadAttendancePolicies(),
            new Date()
          ),
          activityKey
        )
      : null);

  let autoApproved = false;
  let pendingWith = "Reporting Manager";
  let pendingRole = "reporting_manager";
  let pendingLabel = "Reporting Manager";
  let pendingAssigneeDisplay = null;

  if (actionLabel === "Change Shift") {
    pendingLabel = noReportingManager
      ? "HR"
      : resolveChangeShiftPendingWith(formData?.shift);
    pendingWith = pendingLabel;
    pendingRole = noReportingManager ? "HR" : "reporting_manager";
  } else if (actionLabel === CHANGE_WO_ACTION_LABEL) {
    pendingLabel = noReportingManager
      ? "HR"
      : resolveChangeWoPendingWith(formData?.overrideWorkflow || formData?.weeklyOffPolicy);
    pendingWith = pendingLabel;
    pendingRole = noReportingManager ? "HR" : "reporting_manager";
  } else if (wf) {
    autoApproved = !isDirect && mode === "ess" && isActivityAutoApproved(wf);
    const firstStep = (Array.isArray(wf.workflowSteps) ? wf.workflowSteps : []).find(
      (s) => s && !s.notifyOnly
    );
    pendingRole = firstStep?.role || "reporting_manager";
    pendingLabel = autoApproved
      ? "Auto"
      : noReportingManager
        ? "HR"
        : resolveActivityPendingWith(wf);
    pendingWith = pendingLabel;
  }

  if (
    !isDirect &&
    !autoApproved &&
    !noReportingManager &&
    reportingManagerName &&
    isManagerRole(pendingRole)
  ) {
    pendingAssigneeDisplay = reportingManagerName;
    pendingWith = reportingManagerName;
  }

  if (isDirect) {
    pendingWith = null;
    pendingAssigneeDisplay = null;
    pendingRole = null;
    pendingLabel = null;
  }

  const approvalSteps = Array.isArray(wf?.workflowSteps)
    ? wf.workflowSteps.filter((s) => s && !s.notifyOnly)
    : [];

  let trail;
  if (isDirect || autoApproved) {
    trail = [
      {
        status: "Approved",
        role: autoApproved ? "System" : pendingRole || "reporting_manager",
        actor: autoApproved ? "System" : submitter,
        displayText: autoApproved ? "System" : submitter,
        actedAt: now,
        stepOrder: 1,
        note:
          mode === "rm_direct"
            ? "RM direct self-approval (My Team)"
            : mode === "admin_direct"
              ? "Admin direct apply"
              : `Auto-approved by ${actionLabel} workflow`,
      },
    ];
  } else if (approvalSteps.length > 0) {
    trail = approvalSteps.map((step, idx) => {
      const role = step.role || "reporting_manager";
      const isFirst = idx === 0;
      const rmName =
        isFirst && isManagerRole(role) && reportingManagerName
          ? reportingManagerName
          : null;
      return {
        status: isFirst ? "Current" : "Pending",
        role,
        actor: rmName,
        displayText: rmName || roleDisplayName(role) || role,
        actedAt: null,
        stepOrder: idx + 1,
      };
    });
  } else {
    const rmName =
      !noReportingManager && reportingManagerName ? reportingManagerName : null;
    trail = [
      {
        status: "Current",
        role: pendingRole || "reporting_manager",
        actor: rmName,
        displayText: rmName || pendingLabel || "Reporting Manager",
        actedAt: null,
        stepOrder: 1,
        note: noReportingManager
          ? "No reporting manager — queued for HR/Admin"
          : activityKey
            ? `Workflow: ${activityKey}`
            : undefined,
      },
    ];
  }

  return {
    requestId: newRequestId(),
    panelKey: "review",
    actionLabel,
    activityKey: activityKey || null,
    employeeId: employeeId != null ? String(employeeId) : null,
    employeeCode: employeeCode || null,
    userId,
    userName: userName || "Employee",
    userEmail,
    status: isDirect || autoApproved ? "Approved" : "Pending",
    submittedAt: now,
    formData: {
      ...formData,
      employeeCode:
        (formData?.employeeCode && String(formData.employeeCode).trim()) ||
        employeeCode ||
        "",
    },
    pendingWith,
    pendingRole,
    pendingLabel,
    pendingAssigneeDisplay,
    reportingManagerEmployeeId: isDirect || autoApproved ? null : reportingManagerEmployeeId,
    reportingManagerName:
      isDirect || autoApproved ? null : reportingManagerName || null,
    noReportingManager: !!noReportingManager,
    raisedByManager: mode === "rm_direct",
    source:
      mode === "rm_direct" ? "my_team" : mode === "admin_direct" ? "admin_direct" : "sidebar_status_desk",
    raisedByName: raisedByName || null,
    raisedByEmployeeId: raisedByEmployeeId || null,
    workflowTrail: trail,
    activityWorkflow: wf || null,
  };
}

export async function submitChangeShiftRequest({
  employeeId,
  userName,
  userId,
  userEmail,
  formData,
  mode = "ess",
  employeeCode = null,
  raisedByName,
  raisedByEmployeeId,
}) {
  let reportingManagerEmployeeId = null;
  let noReportingManager = false;
  let resolvedEmployeeCode = employeeCode ? String(employeeCode).trim() : "";
  let reportingManagerName = null;
  if (mode === "ess") {
    const rm = await resolveReportingManagerForEmployee(employeeId);
    reportingManagerEmployeeId = rm.reportingManagerEmployeeId;
    reportingManagerName = rm.reportingManagerName;
    noReportingManager = rm.noReportingManager;
    if (!resolvedEmployeeCode) {
      resolvedEmployeeCode = resolveEmployeeCode(rm.employee) || "";
    }
  }
  if (!resolvedEmployeeCode && employeeId) {
    try {
      const empRes = await employeeService.getEmployee(employeeId);
      resolvedEmployeeCode = resolveEmployeeCode(empRes?.data) || "";
    } catch {
      /* ignore */
    }
  }
  const entry = buildAttendanceDeskRequest({
    actionLabel: "Change Shift",
    employeeId,
    userName,
    userId,
    userEmail,
    formData,
    mode,
    employeeCode: resolvedEmployeeCode || null,
    raisedByName,
    raisedByEmployeeId,
    reportingManagerEmployeeId,
    reportingManagerName,
    noReportingManager,
  });
  prependQuickActionRequest(entry);
  await persistAttendanceDeskToServer(entry, { raisedByName });
  return entry;
}

export async function submitChangeWoRequest({
  employeeId,
  userName,
  userId,
  userEmail,
  formData,
  mode = "ess",
  employeeCode = null,
  raisedByName,
  raisedByEmployeeId,
  weeklyOffPolicies = null,
  employeeWeeklyOffMappings = null,
  attendanceLocks = null,
}) {
  let locks = attendanceLocks;
  if (!Array.isArray(locks)) {
    try {
      const { attendanceStructureService } = await import("../services/api");
      const res = await attendanceStructureService.getAttendanceLocks();
      locks = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
    } catch {
      locks = [];
    }
  }

  const employeeStub = { id: employeeId };
  const validation = validateChangeWoRequest({
    employee: employeeStub,
    date: formData?.date,
    proposedDate: formData?.proposedDate,
    weeklyOffPolicies,
    employeeWeeklyOffMappings,
    attendanceLocks: locks,
  });
  if (!validation.ok) {
    throw new Error(validation.message || "Change WO validation failed");
  }

  const policy =
    validation.policy ||
    resolveEmployeeWeeklyOffPolicy(
      employeeStub,
      weeklyOffPolicies,
      employeeWeeklyOffMappings
    );
  const autoApproved =
    mode === "ess" && policy && isChangeWoAutoApproved(policy);
  const submitMode =
    mode === "admin_direct" || mode === "rm_direct"
      ? mode
      : autoApproved
        ? "admin_direct"
        : "ess";

  let reportingManagerEmployeeId = null;
  let reportingManagerName = null;
  let noReportingManager = false;
  let resolvedEmployeeCode = employeeCode ? String(employeeCode).trim() : "";
  if (submitMode === "ess") {
    const rm = await resolveReportingManagerForEmployee(employeeId);
    reportingManagerEmployeeId = rm.reportingManagerEmployeeId;
    reportingManagerName = rm.reportingManagerName;
    noReportingManager = rm.noReportingManager;
    if (!resolvedEmployeeCode) {
      resolvedEmployeeCode = resolveEmployeeCode(rm.employee) || "";
    }
  }
  if (!resolvedEmployeeCode && employeeId) {
    try {
      const empRes = await employeeService.getEmployee(employeeId);
      resolvedEmployeeCode = resolveEmployeeCode(empRes?.data) || "";
    } catch {
      /* ignore */
    }
  }

  const enrichedFormData = {
    ...formData,
    date: validation.dayKey,
    proposedDate: validation.proposedKey,
    currentWoDate: validation.dayKey,
    proposedWoDate: validation.proposedKey,
    weeklyOffType: "full",
    treatAs: "swap_wo",
    overrideWorkflow: policy?.overrideWorkflow || null,
    weeklyOffPolicyId: policy?.id ?? null,
    weeklyOffPolicyName: policy?.name || "",
  };

  if (mode === "admin_direct" || mode === "rm_direct") {
    if (typeof window !== "undefined") {
      const proceed = window.confirm(
        "Change WO cannot run on a locked or processed month (Unprocess / Unlock first). If unused credited Comp Off exists for the current Weekly Off, it will be cancelled. Used Comp Off always blocks Change WO. Continue?"
      );
      if (!proceed) {
        throw new Error("Change WO was not confirmed.");
      }
    }
    enrichedFormData.cancelCreditedCompOff = true;
  }

  const entry = buildAttendanceDeskRequest({
    actionLabel: CHANGE_WO_ACTION_LABEL,
    employeeId,
    userName,
    userId,
    userEmail,
    formData: enrichedFormData,
    mode: submitMode,
    employeeCode: resolvedEmployeeCode || null,
    raisedByName:
      autoApproved && mode === "ess" ? raisedByName || userName : raisedByName,
    raisedByEmployeeId,
    reportingManagerEmployeeId,
    reportingManagerName,
    noReportingManager,
  });
  if (autoApproved && mode === "ess") {
    entry.source = "sidebar_status_desk";
    entry.workflowTrail = [
      {
        status: "Approved",
        actor: userName || "Employee",
        actedAt: Date.now(),
        note: "Auto-approved by Weekly Off override policy",
      },
    ];
  }
  prependQuickActionRequest(entry);
  await persistAttendanceDeskToServer(entry, { raisedByName });
  return entry;
}

export async function submitShortLeaveRequest(opts) {
  return submitAttendanceActionRequest({
    ...opts,
    actionLabel: "Short Leave",
  });
}

export async function submitRegulariseRequest(opts) {
  const entry = await submitAttendanceActionRequest({
    ...opts,
    actionLabel: "Regularise checks",
  });
  await persistAttendanceDeskToServer(entry, { raisedByName: opts.raisedByName });
  const mode = opts.mode || "ess";
  if (mode === "admin_direct" || mode === "rm_direct" || entry?.status === "Approved") {
    try {
      const { syncRegulariseDecision } = await import("./compOffOtNotify");
      await syncRegulariseDecision(entry, "Approved");
    } catch (err) {
      console.warn("Regularise direct apply sync skipped:", err?.message || err);
    }
  }
  return entry;
}

function odRangeFromForm(formData) {
  const fd = formData || {};
  const start = String(fd.date || fd.fromDate || "").slice(0, 10);
  const end = String(fd.toDate || fd.date || fd.fromDate || "").slice(0, 10) || start;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) return null;
  return start <= end ? { start, end } : { start: end, end: start };
}

function onDutyRangesOverlap(a, b) {
  if (!a?.start || !b?.start) return false;
  return a.start <= b.end && b.start <= a.end;
}

export async function assertNoOverlappingOnDutyRequest({
  employeeId,
  formData,
  requestId = null,
}) {
  const mine = odRangeFromForm(formData);
  if (!mine || !employeeId) return;
  const { attendanceService } = await import("../services/api");
  const res = await attendanceService.listDeskQuickRequests({
    employeeId,
    actionLabel: "On Duty",
  });
  const hit = (Array.isArray(res?.data) ? res.data : []).find((row) => {
    if (requestId && String(row.requestId) === String(requestId)) return false;
    const st = String(row.status || "");
    if (st !== "Pending" && st !== "Approved") return false;
    if (String(row.requestType || "").toLowerCase() === "cancellation") return false;
    if (row.cancelledAfterApproval) return false;
    return onDutyRangesOverlap(mine, odRangeFromForm(row.formData || row.formJson || {}));
  });
  if (hit) {
    const span = odRangeFromForm(hit.formData || hit.formJson || {});
    const label =
      span && span.start === span.end
        ? span.start
        : span
          ? `${span.start} to ${span.end}`
          : "overlapping dates";
    throw new Error(
      `An On Duty request already exists for ${label}. Cancel that request first, then create a new one.`
    );
  }
}

function removeQuickActionByRequestId(requestId) {
  saveQuickActionRequests(
    (loadQuickActionRequests() || []).filter(
      (row) => String(row?.requestId) !== String(requestId)
    )
  );
}

/**
 * Generic ESS / RM / Admin desk submit for attendance request labels
 * (On Duty, Comp Off, Overtime, and shared by Regularise/Short Leave helpers).
 */
export async function submitAttendanceActionRequest(opts) {
  const actionLabel = String(opts.actionLabel || "").trim();
  if (!actionLabel) {
    throw new Error("actionLabel is required");
  }
  const mode = opts.mode || "ess";
  let reportingManagerEmployeeId = null;
  let reportingManagerName = null;
  let noReportingManager = false;
  let resolvedEmployeeCode = opts.employeeCode ? String(opts.employeeCode).trim() : "";
  if (mode === "ess") {
    const rm = await resolveReportingManagerForEmployee(opts.employeeId);
    reportingManagerEmployeeId = rm.reportingManagerEmployeeId;
    reportingManagerName = rm.reportingManagerName;
    noReportingManager = rm.noReportingManager;
    if (!resolvedEmployeeCode) {
      resolvedEmployeeCode = resolveEmployeeCode(rm.employee) || "";
    }
  }
  if (!resolvedEmployeeCode && opts.employeeId) {
    try {
      const empRes = await employeeService.getEmployee(opts.employeeId);
      resolvedEmployeeCode = resolveEmployeeCode(empRes?.data) || "";
    } catch {
      /* ignore */
    }
  }
  const entry = buildAttendanceDeskRequest({
    ...opts,
    actionLabel,
    mode,
    employeeCode: resolvedEmployeeCode || null,
    reportingManagerEmployeeId,
    reportingManagerName,
    noReportingManager,
  });
  if (actionLabel === "On Duty") {
    await assertNoOverlappingOnDutyRequest({
      employeeId: entry.employeeId,
      formData: entry.formData || {},
      requestId: entry.requestId,
    });
  }
  prependQuickActionRequest(entry);
  // Short Leave / On Duty / Lock / Unlock: same DB desk as Regularise so Admin + RM retrieve cross-browser.
  if (
    actionLabel === "Short Leave" ||
    actionLabel === "On Duty" ||
    actionLabel === "Attendance Lock" ||
    actionLabel === "Attendance Unlock" ||
    actionLabel === "Attendance Finalization"
  ) {
    try {
      await persistAttendanceDeskToServer(entry, {
        raisedByName: opts.raisedByName,
        throwOnError: actionLabel === "On Duty" || actionLabel === "Attendance Lock" || actionLabel === "Attendance Unlock",
      });
    } catch (err) {
      removeQuickActionByRequestId(entry.requestId);
      const message =
        err?.response?.data?.message || err?.message || `Could not save ${actionLabel} request.`;
      throw new Error(message);
    }
  }
  return entry;
}

export async function submitOnDutyRequest(opts) {
  return submitAttendanceActionRequest({ ...opts, actionLabel: "On Duty" });
}

export async function submitCompOffRequest(opts) {
  const entry = await submitAttendanceActionRequest({
    ...opts,
    actionLabel: "Comp Off",
  });
  try {
    const { attendanceService } = await import("../services/api");
    const date = String(opts?.formData?.date || opts?.formData?.sourceDate || "").slice(0, 10);
    const res = await attendanceService.requestCompOffWork({
      employeeId: opts.employeeId,
      sourceDate: date,
      requestId: entry?.requestId || null,
      notes: opts?.formData?.notes || opts?.formData?.reason || null,
    });
    const mode = opts.mode || "ess";
    if ((mode === "admin_direct" || mode === "rm_direct") && res?.data?.id) {
      await attendanceService.updateCompOffStatus(res.data.id, "approved", { kind: "work" });
    }
  } catch (err) {
    console.warn("Comp Off work API sync failed:", err?.message || err);
    throw err;
  }
  return entry;
}

export async function submitOvertimeRequest(opts) {
  const entry = await submitAttendanceActionRequest({
    ...opts,
    actionLabel: "Overtime",
  });
  try {
    const { attendanceService } = await import("../services/api");
    const fd = opts.formData || {};
    const date = String(fd.date || fd.otDate || "").slice(0, 10);
    let hours = Number(fd.hours || fd.otHours || 0);
    if (!(hours > 0) && fd.startTime && fd.endTime) {
      const [sh, sm] = String(fd.startTime).split(":").map(Number);
      const [eh, em] = String(fd.endTime).split(":").map(Number);
      hours = Math.max(0, (eh * 60 + (em || 0) - (sh * 60 + (sm || 0))) / 60);
    }
    const res = await attendanceService.submitOvertimeClaim({
      employeeId: opts.employeeId,
      otDate: date,
      hours,
      requestId: entry?.requestId || null,
      otType: fd.otType || "working_day",
    });
    const mode = opts.mode || "ess";
    if (
      (mode === "admin_direct" || mode === "rm_direct") &&
      res?.data?.id &&
      String(res.data.status) !== "approved"
    ) {
      await attendanceService.updateOvertimeStatus(res.data.id, "approved");
    }
  } catch (err) {
    console.warn("OT claim API sync failed:", err?.message || err);
    throw err;
  }
  return entry;
}

export async function submitRestrictedHolidayRequest(opts) {
  const raw = opts.formData || {};
  const formData = {
    date: String(raw.date || raw.holidayDate || "").slice(0, 10),
    holidayDate: String(raw.date || raw.holidayDate || "").slice(0, 10),
    holidayId: raw.holidayId ?? null,
    holidayName: raw.holidayName || "",
    rhKey: raw.rhKey || "",
    reason: raw.reason || "",
    notes: raw.notes || "",
    employeeCode: raw.employeeCode || opts.employeeCode || "",
  };
  const entry = await submitAttendanceActionRequest({
    ...opts,
    formData,
    actionLabel: "Restricted Holiday",
  });
  const { attendanceService } = await import("../services/api");
  await attendanceService.selectRestrictedHoliday({
    employeeId: opts.employeeId,
    holidayDate: formData.date || formData.holidayDate,
    holidayId: formData.holidayId || null,
    holidayName: formData.holidayName || formData.reason || null,
    requestId: entry?.requestId || null,
  });
  return entry;
}

export async function submitAttendanceFinalizationDeskRequest({
  actionLabel = "Attendance Lock",
  month,
  year,
  monthLabel,
  reason = "",
  notes = "",
  totals = null,
  totalsDisplay = null,
  payDays = null,
  employeeIds = null,
  attendanceSnapshot = null,
  ...opts
}) {
  const currentUser = getAuthJson("currentUser", null);
  const employeeId =
    opts.employeeId ||
    currentUser?.employeeId ||
    currentUser?.employee_id ||
    Number(getAuthItem("employeeId")) ||
    null;
  const userId = opts.userId || currentUser?.id || null;
  const resolvedName =
    opts.userName ||
    opts.requesterName ||
    [currentUser?.name, currentUser?.lastName].filter(Boolean).join(" ") ||
    currentUser?.name ||
    currentUser?.email ||
    "User";
  const userEmail = opts.userEmail || opts.requesterEmail || currentUser?.email || "";
  const empCode = opts.employeeCode || currentUser?.empCode || currentUser?.employeeCode || null;

  const formData = {
    month,
    year,
    monthLabel: monthLabel || `${month}/${year}`,
    payMonth: `${year}-${String(month).padStart(2, "0")}`,
    reason: reason || "",
    notes: notes || "",
    totals,
    totalsDisplay,
    payDays,
    employeeIds,
    attendanceSnapshot,
    requesterName: resolvedName,
    requesterEmail: userEmail,
    requesterUserId: userId,
    requesterEmpCode: empCode,
    employeeName: resolvedName,
    employeeCode: empCode,
  };
  return submitAttendanceActionRequest({
    employeeId,
    userId,
    userName: resolvedName,
    employeeName: resolvedName,
    employeeCode: empCode,
    userEmail,
    raisedByName: resolvedName,
    ...opts,
    formData,
    actionLabel,
  });
}

function normPersonKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function deskEntryNeedsRmEnrichment(entry) {
  if (!entry || String(entry.status || "") !== "Pending") return false;
  if (entry.noReportingManager) return false;
  const role = entry.pendingRole || entry.formData?.workflowPendingRole || "reporting_manager";
  // Custom-role finalization (e.g. Client Access) must not be treated as RM-routed.
  if (
    !isManagerRole(role) &&
    (entry.actionLabel === "Attendance Lock" ||
      entry.actionLabel === "Attendance Unlock" ||
      entry.actionLabel === "Attendance Finalization" ||
      entry.formData?.workflowPendingRole)
  ) {
    return false;
  }
  if (!isManagerRole(role) && entry.pendingLabel !== "Reporting Manager") {
    // Still enrich RH / RM-routed labels that omit pendingRole
    const label = String(entry.actionLabel || "");
    if (!isRmRoutedLikeLabel(label)) return false;
  }
  const assignee = String(entry.pendingAssigneeDisplay || "").trim();
  if (!assignee || /^reporting\s+manager$/i.test(assignee)) return true;
  if (/^reporting\s+manager$/i.test(String(entry.pendingWith || "").trim())) return true;
  const requester = normPersonKey(entry.userName);
  const trail = Array.isArray(entry.workflowTrail) ? entry.workflowTrail : [];
  return trail.some((step) => {
    const status = String(step?.status || "").toLowerCase();
    if (status === "approved" || status === "rejected") return false;
    const actor = normPersonKey(step?.actor || step?.displayText);
    if (requester && actor === requester) return true;
    if (!step?.role && actor && actor === requester) return true;
    if (!actor || /^reporting\s+manager$/i.test(String(step?.actor || step?.displayText || ""))) {
      return isManagerRole(step?.role || role);
    }
    return false;
  });
}

function isRmRoutedLikeLabel(actionLabel) {
  const label = String(actionLabel || "").trim().toLowerCase();
  return (
    label === "restricted holiday" ||
    label === "on duty" ||
    label === "comp off" ||
    label === "comp off credit" ||
    label === "comp off work" ||
    label === "overtime" ||
    label === "change shift" ||
    label === "change wo" ||
    label === "regularise checks" ||
    label === "short leave" ||
    label === "attendance lock" ||
    label === "attendance unlock" ||
    label === "attendance finalization" ||
    label.includes("weekly off")
  );
}

/**
 * Patch a pending desk entry so Approvals shows RM name (Leave-style),
 * not the role label or the requesting employee.
 */
export function applyReportingManagerDisplayToDeskEntry(entry, reportingManagerName) {
  const name = String(reportingManagerName || "").trim();
  if (!entry || !name) return entry;

  const requester = normPersonKey(entry.userName);
  const raisedBy = normPersonKey(entry.raisedByName);
  const pendingRole = entry.pendingRole || "reporting_manager";
  // Only force RM name when the pending workflow step is actually Reporting Manager.
  // Custom roles (e.g. Client Access on Attendance Unlock) must keep their role label.
  const showRm =
    String(entry.status || "") === "Pending" &&
    !entry.noReportingManager &&
    (isManagerRole(pendingRole) ||
      /^reporting\s+manager$/i.test(String(entry.pendingWith || entry.pendingLabel || "")));

  const trail = Array.isArray(entry.workflowTrail)
    ? entry.workflowTrail.map((step) => {
        const status = String(step?.status || "").toLowerCase();
        if (status === "approved" || status === "rejected") return step;
        const actorRaw = String(step?.actor || step?.displayText || "").trim();
        const actorKey = normPersonKey(actorRaw);
        const role =
          step?.role && (isManagerRole(step.role) || !actorKey || actorKey !== requester)
            ? step.role
            : pendingRole || "reporting_manager";
        const actorIsRequester =
          (requester && actorKey === requester) || (raisedBy && actorKey === raisedBy);
        const actorIsRoleLabel = /^reporting\s+manager$/i.test(actorRaw);
        const needsRm =
          isManagerRole(role) &&
          (actorIsRequester || !actorRaw || actorIsRoleLabel || !step?.role);
        if (!needsRm) return step;
        return {
          ...step,
          role: isManagerRole(step?.role) ? step.role : "reporting_manager",
          actor: name,
          displayText: name,
        };
      })
    : entry.workflowTrail;

  if (!showRm) {
    return { ...entry, workflowTrail: trail, reportingManagerName: entry.reportingManagerName || name };
  }

  return {
    ...entry,
    pendingAssigneeDisplay: name,
    pendingWith: name,
    reportingManagerName: entry.reportingManagerName || name,
    workflowTrail: trail,
  };
}

/**
 * Backfill RM display on pending localStorage desk rows (e.g. older RH requests).
 */
export async function enrichPendingDeskRequestsWithRmNames(list) {
  if (!Array.isArray(list) || !list.length) return list || [];
  const out = list.slice();
  let changed = false;
  const cache = new Map();

  for (let i = 0; i < out.length; i += 1) {
    const entry = out[i];
    if (!deskEntryNeedsRmEnrichment(entry)) continue;
    const empId = entry.employeeId;
    if (empId == null || empId === "") continue;

    const cacheKey = String(empId);
    let rm = cache.get(cacheKey);
    if (!rm) {
      rm = await resolveReportingManagerForEmployee(empId);
      cache.set(cacheKey, rm);
    }
    if (!rm?.reportingManagerName) continue;

    const patched = applyReportingManagerDisplayToDeskEntry(entry, rm.reportingManagerName);
    if (rm.reportingManagerEmployeeId && !patched.reportingManagerEmployeeId) {
      patched.reportingManagerEmployeeId = rm.reportingManagerEmployeeId;
    }
    out[i] = patched;
    changed = true;
  }

  if (changed) saveQuickActionRequests(out);
  return out;
}
